import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
  numberRegex, specialCharacterAndEmojiNameRegex, specialCharacterAndEmojiRegex,
} from '@shared/address-form/address-validation.constant';

const SPECIAL_CHARACTERS: string = 'specialCharacters';
const NUMBERS: string = 'numbers';
const WORD_LIMIT: string = 'wordLimit';
const MIN_CHAR_LIMIT: string = 'minCharLimit';
const MAX_CHAR_LIMIT: string = 'maxCharLimit';

export type AddressFieldError = { errorType: string } & { [flag: string]: boolean | string };
export type AddressFieldErrors = Record<string, AddressFieldError>;

export function createAddressFieldErrors(): AddressFieldErrors {
  return {
    contactName: { contactNameError: false, errorType: '' },
    city: { cityError: false, errorType: '' },
    buildingDetails: { buildingDetailsError: false, errorType: '' },
    localityDetails: { localityDetailsError: false, errorType: '' },
    landmark: { landmarkError: false, errorType: '' },
  };
}

function setFieldError(errors: AddressFieldErrors, fieldName: string, errorType: string): void {
  if (errors[fieldName]) {
    errors[fieldName][`${fieldName}Error`] = true;
    errors[fieldName].errorType = errorType;
  }
}

function clearFieldError(errors: AddressFieldErrors, fieldName: string): void {
  if (errors[fieldName]) {
    errors[fieldName][`${fieldName}Error`] = false;
    errors[fieldName].errorType = '';
  }
}

export function createTextFieldValidator(
  fieldName: string,
  errors: AddressFieldErrors,
  options?: { minChars?: number; maxWords?: number },
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const { value }: { value: string } = control;
    const nonSpaceCount = (value?.match(/\S/g) || []).length;
    const wordCount = value?.split(' ').filter((word: string): boolean => word.trim() !== '').length;

    if (specialCharacterAndEmojiRegex.test(value)) {
      setFieldError(errors, fieldName, SPECIAL_CHARACTERS);
      return { invalidCharacter: true };
    }

    if (options?.minChars !== undefined) {
      if (nonSpaceCount < options.minChars) {
        setFieldError(errors, fieldName, MIN_CHAR_LIMIT);
        return { message: 'error' };
      }
    }

    if (options?.maxWords !== undefined) {
      if (wordCount > options.maxWords) {
        setFieldError(errors, fieldName, WORD_LIMIT);
        return { message: 'error' };
      }
    }

    clearFieldError(errors, fieldName);
    return null;
  };
}

export function createNameValidator(errors: AddressFieldErrors): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const fieldName = 'contactName';
    const { value }: { value: string } = control;
    const minCharValue = 3;
    const maxCharValue = 25;
    const nonSpaceCount = (value?.match(/\S/g) || []).length;
    const wordCount = value?.split(' ').length;

    const validations = [
      {
        condition: numberRegex.test(value),
        errorConstant: NUMBERS,
        errorResponse: { invalidCharacter: true },
      },
      {
        condition: specialCharacterAndEmojiNameRegex.test(value),
        errorConstant: SPECIAL_CHARACTERS,
        errorResponse: { message: 'error' },
      },
      {
        condition: nonSpaceCount < minCharValue,
        errorConstant: MIN_CHAR_LIMIT,
        errorResponse: { message: 'error' },
      },
      {
        condition: nonSpaceCount > maxCharValue,
        errorConstant: MAX_CHAR_LIMIT,
        errorResponse: { message: 'error' },
      },
      {
        condition: wordCount > 3,
        errorConstant: WORD_LIMIT,
        errorResponse: { message: 'error' },
      },
    ];

    const failedValidation = validations.find((validation: { condition: boolean }): boolean => validation.condition);
    if (failedValidation) {
      setFieldError(errors, fieldName, failedValidation.errorConstant);
      return failedValidation.errorResponse;
    }

    clearFieldError(errors, fieldName);
    return null;
  };
}
